import { inject, Service } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { AppConfig } from '../app-config';
import { Resposta } from '../../../core/models/base/base';
import { LoginRequest, LoginResponse } from '../../../core/models/auth/auth';

@Service()
export class Auth {
    private static readonly TOKEN_COOKIE_NAME = 'auth_token';
    private static readonly LOGIN_PATH = '/auth/login';

    private readonly http = inject(HttpClient);
    private readonly appConfig = inject(AppConfig);

    private readonly autenticado$ = new BehaviorSubject<boolean>(this.possuiTokenValido());

    public get estaAutenticado$(): Observable<boolean> {
        return this.autenticado$.asObservable();
    }

    /**
     * Autentica e guarda o token. O erro HTTP é propagado de propósito: quem chama precisa das
     * mensagens do envelope da API para dizer o que aconteceu (ex.: "Login ou senha inválidos").
     */
    public login(usuario: string, senha: string): Observable<boolean> {
        const url = `${this.appConfig.apiUrl()}${Auth.LOGIN_PATH}`;
        const body: LoginRequest = { login: usuario, senha };

        return this.http.post<Resposta<LoginResponse>>(url, body).pipe(
            map((resposta) => {
                const token = resposta.dados?.token;
                if (!token) return false;
                this.salvarToken(token);
                this.autenticado$.next(true);
                return true;
            })
        );
    }

    public possuiToken(): boolean {
        return !!this.getToken();
    }

    public possuiTokenValido(): boolean {
        const token = this.getToken();
        return !!token && !this.expirado(token);
    }

    public getToken(): string | null {
        const cookies = document.cookie.split(';');
        const tokenCookie = cookies.find((c) => c.trim().startsWith(`${Auth.TOKEN_COOKIE_NAME}=`));
        if (!tokenCookie) return null;
        return tokenCookie.split('=')[1] ?? null;
    }

    public logout(): void {
        document.cookie = `${Auth.TOKEN_COOKIE_NAME}=; path=/; max-age=0; SameSite=Strict`;
        this.autenticado$.next(false);
    }

    private salvarToken(token: string): void {
        const diaEmSegundos = 60 * 60 * 24;
        document.cookie = `${Auth.TOKEN_COOKIE_NAME}=${token}; path=/; max-age=${diaEmSegundos}; SameSite=Strict`;
    }

    /**
     * Token ilegível (payload corrompido, JSON inválido ou sem `exp`) conta como expirado: é
     * preferível mandar o usuário para o login do que confiar num token que não sabemos ler.
     */
    private expirado(token: string): boolean {
        const exp = this.lerExpiracao(token);
        if (exp === null) return true;

        return exp * 1000 <= Date.now();
    }

    private lerExpiracao(token: string): number | null {
        const payload = token.split('.')[1];
        if (!payload) return null;

        try {
            const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
            const exp = (JSON.parse(json) as { exp?: number }).exp;
            return typeof exp === 'number' ? exp : null;
        } catch {
            return null;
        }
    }
}
